import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  InputAdornment,
  FormControlLabel,
  Switch,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';

const DEFAULT_PHONE_PREFIX = '+7';
const MAX_PHONE_DIGITS = 11;
const RUSSIAN_PHONE_PREFIX = '7';

const getPhoneDigits = (phone) => phone.replace(/\D/g, '');

const normalizePhone = (value, forceRussianPrefix = false) => {
  let digits = getPhoneDigits(value);

  if (forceRussianPrefix) {
    if (digits.startsWith('8') && digits.length > 10) {
      digits = `${RUSSIAN_PHONE_PREFIX}${digits.slice(1)}`;
    } else if (!digits.startsWith(RUSSIAN_PHONE_PREFIX)) {
      digits = `${RUSSIAN_PHONE_PREFIX}${digits}`;
    }
  }

  const normalizedDigits = digits.slice(0, MAX_PHONE_DIGITS);
  return normalizedDigits ? `+${normalizedDigits}` : (forceRussianPrefix ? DEFAULT_PHONE_PREFIX : '');
};

const MasterFormDialog = ({ open, onClose, onSave, master }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    patronymic: '',
    phone: DEFAULT_PHONE_PREFIX,
  });

  useEffect(() => {
    if (master) {
      setFormData({
        firstName: master.firstName || '',
        lastName: master.lastName || '',
        patronymic: master.patronymic || '',
        phone: master.phone || '',
        isActive: master.isActive ?? true,
      });
    } else {
      setFormData({
        firstName: '',
        lastName: '',
        patronymic: '',
        phone: DEFAULT_PHONE_PREFIX,
      });
    }
  }, [master, open]);

  const handleChange = (e) => {
    const { name, value, checked } = e.target;

    if (name === 'phone') {
      setFormData(prev => ({
        ...prev,
        phone: normalizePhone(value, !master),
      }));
      return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: name === 'isActive' ? checked : value,
    }));
  };

  const handleSubmit = () => {
    const { lastName, firstName, patronymic, phone, isActive } = formData;
    const data = master
      ? { lastName, firstName, patronymic, phone, isActive }
      : { lastName, firstName, patronymic, phone };
    onSave(data);
  };

  const phoneDigits = getPhoneDigits(formData.phone);
  const hasPhoneNumber = master
    ? phoneDigits.length > 0
    : phoneDigits.length > RUSSIAN_PHONE_PREFIX.length;
  const isValid = formData.firstName.trim().length > 0
    && hasPhoneNumber
    && phoneDigits.length <= MAX_PHONE_DIGITS;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          backgroundColor: '#fafafa',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              backgroundColor: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <PersonIcon sx={{ color: 'white', fontSize: 22 }} />
          </Box>
          <Typography variant="h6" fontWeight={600}>
            {master ? 'Редактирование исполнителя' : 'Новый исполнитель'}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="large">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        <TextField
          fullWidth
          name="lastName"
          label="Фамилия"
          value={formData.lastName}
          onChange={handleChange}
          placeholder="Введите фамилию"
          sx={{ mb: 2.5, mt: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <PersonIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
              </InputAdornment>
            ),
            sx: { borderRadius: 2 },
          }}
        />

        <TextField
          fullWidth
          name="firstName"
          label="Имя"
          value={formData.firstName}
          onChange={handleChange}
          placeholder="Введите имя"
          required
          sx={{ mb: 2.5 }}
          InputProps={{ sx: { borderRadius: 2 } }}
        />

        <TextField
          fullWidth
          name="patronymic"
          label="Отчество"
          value={formData.patronymic}
          onChange={handleChange}
          placeholder="Введите отчество"
          sx={{ mb: 2.5 }}
          InputProps={{ sx: { borderRadius: 2 } }}
        />

        <TextField
          fullWidth
          name="phone"
          label="Телефон"
          value={formData.phone}
          onChange={handleChange}
          placeholder="+7 (___) ___-__-__"
          type="tel"
          required
          sx={{ mb: 2.5 }}
          inputProps={{ inputMode: 'tel' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <PhoneIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
              </InputAdornment>
            ),
            sx: { borderRadius: 2 },
          }}
        />

        {master && (
          <FormControlLabel
            control={
              <Switch
                name="isActive"
                checked={formData.isActive}
                onChange={handleChange}
                color="primary"
              />
            }
            label="Активен"
          />
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, backgroundColor: '#fafafa', borderTop: '1px solid', borderColor: 'divider' }}>
        <Button onClick={onClose} variant="outlined" color="error" sx={{ borderRadius: 2, textTransform: 'none' }}>
          Отмена
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!isValid}
          sx={{ borderRadius: 2, px: 3, textTransform: 'none', fontWeight: 600 }}
        >
          {master ? 'Сохранить' : 'Создать'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MasterFormDialog;
